import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { Plus, Trash2, FileText, Search, FolderOpen, Upload, Download, Loader2, File, Image, FileSpreadsheet, Sparkles, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const DOC_TYPES = ['transcript', 'test_score', 'report_card', 'resume', 'recommendation', 'financial', 'other'];
const DOC_TYPE_LABELS = { transcript: 'Transcript', test_score: 'Test Score', report_card: 'Report Card', resume: 'Resume', recommendation: 'Recommendation', financial: 'Financial', other: 'Other' };
const EXTRACTABLE_TYPES = new Set(['transcript', 'test_score', 'report_card']);

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ type }) {
  if (type?.startsWith('image/')) return <Image className="h-5 w-5 text-purple-500" />;
  if (type === 'application/pdf') return <FileText className="h-5 w-5 text-red-500" />;
  if (type?.includes('spreadsheet') || type?.includes('csv')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
  return <File className="h-5 w-5 text-[#0F392B]" />;
}

export default function DocumentVault() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [extracting, setExtracting] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', document_type: 'other', notes: '' });
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => {
    api.getDocuments().then(setDocs).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setFile(f);
    if (!form.name) setForm(prev => ({ ...prev, name: f.name.replace(/\.[^.]+$/, '') }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      await api.createDocument(form, file);
      toast.success(file ? 'Document uploaded!' : 'Document added!');
      setForm({ name: '', document_type: 'other', notes: '' });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setOpen(false);
      load();
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    try {
      const blob = await api.downloadDocument(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename || doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error('Download failed'); }
    finally { setDownloading(null); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.deleteDocument(id); toast.success('Deleted'); load(); } catch (err) { toast.error(err.message); }
  };

  const handleExtract = async (doc) => {
    setExtracting(doc.id);
    try {
      const result = await api.extractDocumentData(doc.id);
      toast.success(result.message || 'Data extracted and populated!');
      load();
    } catch (err) {
      toast.error(err.message || 'Extraction failed. Try a text-based PDF.');
    } finally {
      setExtracting(null);
    }
  };

  const filtered = docs.filter((d) => d.name.toLowerCase().includes(search.toLowerCase()));
  const totalSize = docs.reduce((s, d) => s + (d.file_size || 0), 0);

  if (loading) return <div className="max-w-3xl mx-auto space-y-4 animate-pulse">{[1,2,3].map(i => <div key={i} className="h-20 bg-[#E2E2E2] rounded-xl" />)}</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="document-vault-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Documents</h1>
          <p className="text-xs text-muted-foreground mt-1">
            {docs.length} documents {totalSize > 0 && `· ${formatFileSize(totalSize)} total`}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-xs h-8" data-testid="add-document-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>Upload Document</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {/* File Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${file ? 'border-[#0F392B] bg-[#0F392B]/5' : 'border-[#E2E2E2] hover:border-[#0F392B]/30'}`}
                onClick={() => fileInputRef.current?.click()}
                data-testid="file-drop-zone"
              >
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.csv" data-testid="file-input" />
                {file ? (
                  <div className="flex items-center gap-3 justify-center">
                    <FileIcon type={file.type} />
                    <div className="text-left">
                      <p className="text-sm font-medium truncate max-w-[250px]">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Click to select a file</p>
                    <p className="text-[10px] text-muted-foreground mt-1">PDF, DOC, DOCX, JPG, PNG, TXT, CSV — max 10MB</p>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Document Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., SAT Score Report" required data-testid="doc-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Type</Label>
                  <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                    <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{DOC_TYPE_LABELS[t]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Notes (optional)</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional notes..." data-testid="doc-notes-input" />
              </div>

              <Button type="submit" className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" disabled={uploading} data-testid="doc-submit-btn">
                {uploading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading...</> : file ? 'Upload Document' : 'Add Document'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9 border-[#E2E2E2]" placeholder="Search documents..." value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="doc-search" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <FolderOpen className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No documents yet</p>
          <p className="text-xs text-muted-foreground mt-1">Upload transcripts, test scores, and other application documents</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((d) => (
            <Card key={d.id} className="border-[#E2E2E2]" data-testid={`doc-card-${d.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#F9F9F7] flex items-center justify-center flex-shrink-0">
                  <FileIcon type={d.content_type} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium truncate">{d.name}</h3>
                    <Badge variant="outline" className="text-[10px] capitalize">{DOC_TYPE_LABELS[d.document_type] || d.document_type}</Badge>
                    {d.has_file && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px]">File</Badge>}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {d.original_filename && <span className="text-xs text-muted-foreground truncate">{d.original_filename}</span>}
                    {d.file_size && <span className="text-xs text-muted-foreground">{formatFileSize(d.file_size)}</span>}
                    <span className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</span>
                  </div>
                  {d.notes && <p className="text-xs text-muted-foreground mt-1">{d.notes}</p>}
                  {d.populated_fields?.length > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                      <span className="text-[10px] text-emerald-600">
                        Auto-populated: {d.populated_fields.join(', ')}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {d.has_file && EXTRACTABLE_TYPES.has(d.document_type) && !d.populated_fields?.length && (
                    <Button size="sm" variant="ghost" onClick={() => handleExtract(d)} disabled={extracting === d.id}
                      className="h-8 px-2 text-[10px] gap-1 text-[#D4AF37] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                      data-testid={`extract-doc-${d.id}`}>
                      {extracting === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      {extracting === d.id ? 'Extracting...' : 'Extract Data'}
                    </Button>
                  )}
                  {d.has_file && (
                    <Button size="sm" variant="ghost" onClick={() => handleDownload(d)} disabled={downloading === d.id}
                      className="h-8 w-8 p-0" data-testid={`download-doc-${d.id}`}>
                      {downloading === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id, d.name)}
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500" data-testid={`delete-doc-${d.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
